const escapeHtml = (value) => {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
};

const normalizeCodeLanguage = (value) => {
  if (!value) {
    return '';
  }
  const cleaned = value
    .toString()
    .replace(/^language-/i, '')
    .trim()
    .replace(/[^a-zA-Z0-9_-]/g, '')
    .toLowerCase();
  return cleaned ? `language-${cleaned}` : '';
};

const stripQuotes = (value) => {
  if (!value) {
    return value;
  }
  const trimmed = value.trim();
  if (
    (trimmed.startsWith('"') && trimmed.endsWith('"')) ||
    (trimmed.startsWith("'") && trimmed.endsWith("'"))
  ) {
    return trimmed.slice(1, -1);
  }
  return trimmed;
};

const parseKeyValue = (line) => {
  const quotedDouble = /^"([^"]+)"\s*:\s*(.*)$/.exec(line);
  if (quotedDouble) {
    return { key: quotedDouble[1], rawValue: quotedDouble[2] };
  }
  const quotedSingle = /^'([^']+)'\s*:\s*(.*)$/.exec(line);
  if (quotedSingle) {
    return { key: quotedSingle[1], rawValue: quotedSingle[2] };
  }
  const plain = /^([A-Za-z0-9_@.-]+)\s*:\s*(.*)$/.exec(line);
  if (plain) {
    return { key: plain[1], rawValue: plain[2] };
  }
  return null;
};

const findNextContentLine = (lines, startIndex) => {
  for (let i = startIndex; i < lines.length; i += 1) {
    const trimmed = lines[i].trim();
    if (!trimmed) {
      continue;
    }
    if (trimmed === '---') {
      return null;
    }
    return { line: lines[i], indent: lines[i].match(/^(\s*)/)[1].length };
  }
  return null;
};

const preprocessMathBlocks = (markdown) => {
  const lines = markdown.split(/\r?\n/);
  const output = [];
  let inCodeBlock = false;

  for (let index = 0; index < lines.length; index += 1) {
    const line = lines[index];
    const trimmed = line.trim();

    if (trimmed.startsWith('```')) {
      inCodeBlock = !inCodeBlock;
      output.push(line);
      continue;
    }

    if (inCodeBlock) {
      output.push(line);
      continue;
    }

    if (trimmed === '$$') {
      const collected = [];
      let cursor = index + 1;
      let closed = false;

      for (; cursor < lines.length; cursor += 1) {
        const current = lines[cursor];
        if (current.trim() === '$$') {
          closed = true;
          break;
        }
        collected.push(current);
      }

      const content = collected.join('\n').trim();
      if (content) {
        output.push(`<div class="math-block">$$${escapeHtml(content)}$$</div>`);
      }

      if (closed) {
        index = cursor;
      } else {
        index = lines.length;
      }
      continue;
    }

    if (trimmed.startsWith('$$') && trimmed.endsWith('$$') && trimmed.length > 4) {
      output.push(`<div class="math-block">$$${escapeHtml(trimmed.slice(2, -2).trim())}$$</div>`);
      continue;
    }

    output.push(line);
  }

  return output.join('\n');
};

const parseFrontMatter = (markdown) => {
  const normalized = markdown.replace(/^\uFEFF/, '');
  if (!normalized.startsWith('---')) {
    return { frontmatter: {}, body: markdown };
  }

  const lines = normalized.split(/\r?\n/);
  if (lines[0].trim() !== '---') {
    return { frontmatter: {}, body: markdown };
  }

  const frontmatter = {};
  const stack = [{ indent: -1, value: frontmatter }];
  let i = 1;

  for (; i < lines.length; i += 1) {
    const line = lines[i];
    const trimmed = line.trim();
    if (trimmed === '---') {
      i += 1;
      break;
    }
    if (!trimmed) {
      continue;
    }

    const indent = line.match(/^(\s*)/)[1].length;
    while (stack.length > 1 && indent <= stack[stack.length - 1].indent) {
      stack.pop();
    }
    const current = stack[stack.length - 1].value;

    if (trimmed.startsWith('- ')) {
      if (!Array.isArray(current)) {
        continue;
      }
      current.push(stripQuotes(trimmed.slice(2)));
      continue;
    }

    const parsedKey = parseKeyValue(trimmed);
    if (!parsedKey) {
      continue;
    }

    const { key, rawValue } = parsedKey;
    if (!rawValue) {
      const nextLine = findNextContentLine(lines, i + 1);
      const shouldBeArray =
        nextLine && nextLine.indent > indent && nextLine.line.trim().startsWith('- ');
      const nestedValue = shouldBeArray ? [] : {};
      current[key] = nestedValue;
      stack.push({ indent, value: nestedValue });
      continue;
    }

    if (rawValue.startsWith('[') && rawValue.endsWith(']')) {
      const inner = rawValue.slice(1, -1).trim();
      current[key] = inner
        ? inner
            .split(',')
            .map((item) => stripQuotes(item.trim()))
            .filter(Boolean)
        : [];
      continue;
    }

    if ((key === 'tags' || key === 'keywords') && rawValue.includes(',')) {
      current[key] = rawValue
        .split(',')
        .map((item) => stripQuotes(item))
        .map((item) => item.trim())
        .filter(Boolean);
      continue;
    }

    current[key] = stripQuotes(rawValue);
  }

  if (i >= lines.length) {
    return { frontmatter: {}, body: markdown };
  }

  return {
    frontmatter,
    body: lines.slice(i).join('\n'),
  };
};

const estimateReadingTime = (markdown) => {
  const text = markdown
    .replace(/```[\s\S]*?```/g, ' ')
    .replace(/`[^`]*`/g, ' ')
    .replace(/\[([^\]]+)\]\(([^)]+)\)/g, '$1')
    .replace(/[#>*_-]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

  const words = text ? text.split(' ').length : 0;
  return Math.max(1, Math.round(words / 200));
};

const mergeUnique = (base, entries) => {
  const values = Array.isArray(base) ? base.slice() : [];
  for (const value of entries) {
    if (!values.includes(value)) {
      values.push(value);
    }
  }
  return values;
};

const walkTree = (node, visitor) => {
  if (!node || typeof node !== 'object') {
    return;
  }

  visitor(node);

  if (!Array.isArray(node.children)) {
    return;
  }

  node.children.forEach((child) => walkTree(child, visitor));
};

const rehypeNormalizeCodeLanguage = () => {
  return (tree) => {
    walkTree(tree, (node) => {
      if (node.type !== 'element' || node.tagName !== 'code') {
        return;
      }

      const properties = node.properties || {};
      const classNameValue = properties.className;
      const classList = Array.isArray(classNameValue)
        ? classNameValue.map((item) => item.toString())
        : typeof classNameValue === 'string'
          ? classNameValue.split(/\s+/).filter(Boolean)
          : [];

      const languageClass = classList.find((item) => /^language-/i.test(item));
      const normalizedLanguageClass = normalizeCodeLanguage(languageClass);
      const preserved = classList.filter((item) => !/^language-/i.test(item));
      if (normalizedLanguageClass) {
        preserved.unshift(normalizedLanguageClass);
      }

      node.properties = {
        ...properties,
        className: preserved,
      };
    });
  };
};

const rehypeSecureExternalLinks = () => {
  return (tree) => {
    walkTree(tree, (node) => {
      if (node.type !== 'element' || node.tagName !== 'a') {
        return;
      }

      const properties = node.properties || {};
      if (properties.target !== '_blank') {
        return;
      }

      const relValue = properties.rel;
      const relParts = Array.isArray(relValue)
        ? relValue.map((item) => item.toString())
        : typeof relValue === 'string'
          ? relValue.split(/\s+/).filter(Boolean)
          : [];

      const nextRel = new Set(relParts);
      nextRel.add('noopener');
      nextRel.add('noreferrer');

      node.properties = {
        ...properties,
        rel: Array.from(nextRel),
      };
    });
  };
};

let remarkProcessor = null;
let remarkProcessorInitialized = false;
const strictMarkdownRuntime =
  process.env.NODE_ENV === 'test' && process.env.MARKDOWN_RUNTIME_STRICT === 'true';

const createRemarkProcessor = () => {
  if (remarkProcessorInitialized) {
    return remarkProcessor;
  }

  remarkProcessorInitialized = true;

  try {
    const { unified } = require('unified');
    const remarkParseModule = require('remark-parse');
    const remarkGfmModule = require('remark-gfm');
    const remarkRehypeModule = require('remark-rehype');
    const rehypeRawModule = require('rehype-raw');
    const rehypeSanitizeModule = require('rehype-sanitize');
    const rehypeStringifyModule = require('rehype-stringify');

    const remarkParse = remarkParseModule.default || remarkParseModule;
    const remarkGfm = remarkGfmModule.default || remarkGfmModule;
    const remarkRehype = remarkRehypeModule.default || remarkRehypeModule;
    const rehypeRaw = rehypeRawModule.default || rehypeRawModule;
    const rehypeSanitize = rehypeSanitizeModule.default || rehypeSanitizeModule;
    const rehypeStringify = rehypeStringifyModule.default || rehypeStringifyModule;
    const defaultSchema = rehypeSanitizeModule.defaultSchema || {};

    const sanitizeSchema = {
      ...defaultSchema,
      tagNames: mergeUnique(defaultSchema.tagNames, [
        'figure',
        'figcaption',
        'div',
        'span',
        'table',
        'thead',
        'tbody',
        'tr',
        'th',
        'td',
      ]),
      attributes: {
        ...defaultSchema.attributes,
        a: mergeUnique(defaultSchema.attributes?.a, ['href', 'name', 'target', 'rel', 'title']),
        img: mergeUnique(defaultSchema.attributes?.img, [
          'src',
          'alt',
          'title',
          'width',
          'height',
          'loading',
          'decoding',
        ]),
        code: mergeUnique(defaultSchema.attributes?.code, ['className']),
        div: mergeUnique(defaultSchema.attributes?.div, ['className']),
        span: mergeUnique(defaultSchema.attributes?.span, ['className']),
        figure: mergeUnique(defaultSchema.attributes?.figure, ['className']),
        figcaption: mergeUnique(defaultSchema.attributes?.figcaption, ['className']),
        th: mergeUnique(defaultSchema.attributes?.th, ['align']),
        td: mergeUnique(defaultSchema.attributes?.td, ['align']),
      },
    };

    remarkProcessor = unified()
      .use(remarkParse)
      .use(remarkGfm)
      .use(remarkRehype, { allowDangerousHtml: true })
      .use(rehypeRaw)
      .use(rehypeNormalizeCodeLanguage)
      .use(rehypeSecureExternalLinks)
      .use(rehypeSanitize, sanitizeSchema)
      .use(rehypeStringify);
  } catch (error) {
    if (strictMarkdownRuntime) {
      throw error;
    }
    remarkProcessor = null;
  }

  return remarkProcessor;
};

const markdownToHtml = (markdown) => {
  const preparedMarkdown = preprocessMathBlocks(markdown);
  const processor = createRemarkProcessor();

  if (!processor) {
    const fallback = preparedMarkdown.trim();
    return fallback ? `<p>${escapeHtml(fallback)}</p>` : '';
  }

  try {
    return String(processor.processSync(preparedMarkdown));
  } catch (error) {
    if (strictMarkdownRuntime) {
      throw error;
    }
    const fallback = preparedMarkdown.trim();
    return fallback ? `<p>${escapeHtml(fallback)}</p>` : '';
  }
};

export const parseMarkdownWithRemark = (markdown) => {
  const { frontmatter, body } = parseFrontMatter(markdown);
  const html = markdownToHtml(body);
  const readingTime = estimateReadingTime(body);

  return {
    frontmatter,
    html,
    readingTime,
  };
};
